"use client";

import { track, type PostHogEventMap } from "@/lib/posthog/events";
import {
  sanitizeGamificationResultCapture,
  type GamificationResultCaptureMetadata,
} from "@/lib/gamification/result-capture";

export type GamificationTrackingContext = {
  readonly componentId: string;
  readonly componentFamily: string;
  readonly campaignKey?: string;
  readonly contentTheme?: string;
  readonly experienceId?: string;
  readonly marketingComplianceGate?: string;
  readonly routeTemplateId?: string;
  readonly sourceLocale?: string;
  readonly sourcePath?: string;
  readonly taxonomyVersion?: string;
  readonly toolKey?: string;
  readonly variantKey?: string;
  readonly vertical18Plus?: boolean;
};

export type GamificationTrackingOverrides = Partial<PostHogEventMap["component_engaged"]>;

export function gamificationTrackingAttributes(context: GamificationTrackingContext) {
  return {
    "data-analytics-component-family": context.componentFamily,
    "data-analytics-component-id": context.componentId,
    ...(context.experienceId ? { "data-analytics-experience-id": context.experienceId } : {}),
    ...(context.variantKey ? { "data-analytics-variant-key": context.variantKey } : {}),
    ...(context.campaignKey ? { "data-analytics-campaign-key": context.campaignKey } : {}),
  } as const;
}

export function trackGamificationEngagement(
  context: GamificationTrackingContext,
  ctaId: string,
  overrides: GamificationTrackingOverrides = {},
) {
  const payload = {
    campaign_key: context.campaignKey,
    component_family: context.componentFamily,
    component_id: context.componentId,
    content_theme: context.contentTheme,
    cta_id: ctaId,
    experience_id: context.experienceId,
    marketing_compliance_gate: context.marketingComplianceGate,
    route_template_id: context.routeTemplateId,
    source_locale: context.sourceLocale,
    source_path: context.sourcePath,
    taxonomy_version: context.taxonomyVersion ?? "gamification_audit_2026_06_18_wave_1",
    tool_key: context.toolKey,
    variant_key: context.variantKey,
    vertical_18plus: context.vertical18Plus,
    ...overrides,
  } satisfies PostHogEventMap["component_engaged"];

  if (typeof window !== "undefined" && typeof window.__seohPostHogCaptureComponentEngagement === "function") {
    return window.__seohPostHogCaptureComponentEngagement(payload);
  }

  return track("component_engaged", payload);
}

export function captureGamificationResultForCrm(
  context: GamificationTrackingContext,
  resultTier: string,
  metadata: GamificationResultCaptureMetadata = {},
) {
  const payload = sanitizeGamificationResultCapture({
    ...metadata,
    route_template_id: context.routeTemplateId,
    source_locale: context.sourceLocale,
    source_page: context.sourcePath,
    source_template: "components/gamification",
    content_theme: context.contentTheme,
    tool_key: context.toolKey,
    result_tier: resultTier,
    vertical_18plus: context.vertical18Plus,
    vertical_18plus_reason: context.vertical18Plus ? metadata.vertical_18plus_reason ?? "service_slug" : undefined,
    marketing_compliance_gate: context.vertical18Plus ? "pending" : "not_required",
    service_line: context.vertical18Plus ? "adult" : metadata.service_line,
  });

  if (typeof window === "undefined" || Object.keys(payload).length === 0) {
    return payload;
  }

  const target = window as Window & {
    __seohCaptureGamificationResult?: (value: GamificationResultCaptureMetadata) => void;
  };

  if (typeof target.__seohCaptureGamificationResult === "function") {
    target.__seohCaptureGamificationResult(payload);
  } else {
    window.dispatchEvent(new CustomEvent("seoh:gamification-result", { detail: payload }));
  }

  return payload;
}
